import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import DashboardSidebar from '../components/shared/DashboardSidebar';
import Footer from '../components/shared/Footer';
import ProductCard from '../components/shared/ProductCard';
import AIMarketInsight from '../components/shared/AIMarketInsight';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, Filter, Grid3X3, List, X, Loader2,
  Wheat, Factory, Shirt, Pill, Building2, Laptop, Palette, Cog,
  SlidersHorizontal
} from 'lucide-react';

const categories = [
  { id: 'agriculture', name: 'Agriculture', icon: Wheat },
  { id: 'agro-processing', name: 'Agro-Processing', icon: Factory },
  { id: 'manufacturing', name: 'Manufacturing', icon: Cog },
  { id: 'textiles', name: 'Textiles', icon: Shirt },
  { id: 'pharmaceuticals', name: 'Pharmaceuticals', icon: Pill },
  { id: 'construction', name: 'Construction', icon: Building2 },
  { id: 'digital-services', name: 'Digital Services', icon: Laptop },
  { id: 'creative-industries', name: 'Creative Industries', icon: Palette },
];

const countries = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco', 
  'Tanzania', 'Ethiopia', 'Rwanda', 'Senegal', 'Cameroon', 'Uganda'
];

// Sample products for demo
const sampleProducts = [
  {
    product_id: 'prod_001',
    name: 'Premium Cocoa Beans from Ghana',
    description: 'High-quality cocoa beans, ideal for chocolate production. Sourced from sustainable farms.',
    category: 'agriculture',
    price: 3500,
    currency: 'USD',
    moq: 100,
    country_of_origin: 'Ghana',
    seller_name: 'Ghana Cocoa Board',
    verification_status: 'verified',
    afcfta_eligible: true,
    images: ['https://images.unsplash.com/photo-1606312619070-d48b4c652a52?w=400&h=300&fit=crop'],
  },
  {
    product_id: 'prod_002',
    name: 'Ethiopian Arabica Coffee Beans',
    description: 'Premium single-origin Arabica coffee from the highlands of Ethiopia. Rich aroma and flavor.',
    category: 'agriculture',
    price: 4200,
    currency: 'USD',
    moq: 50,
    country_of_origin: 'Ethiopia',
    seller_name: 'Ethiopian Coffee Exports',
    verification_status: 'verified',
    afcfta_eligible: true,
    images: ['https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400&h=300&fit=crop'],
  },
  {
    product_id: 'prod_003',
    name: 'African Print Textiles',
    description: 'Vibrant Ankara fabrics, 100% cotton. Perfect for fashion and home décor.',
    category: 'textiles',
    price: 25,
    currency: 'USD',
    moq: 200,
    country_of_origin: 'Nigeria',
    seller_name: 'Lagos Textile Mills',
    verification_status: 'verified',
    afcfta_eligible: true,
    images: ['https://images.unsplash.com/photo-1768212565426-58b089b6386d?w=400&h=300&fit=crop'],
  },
  {
    product_id: 'prod_004',
    name: 'Kenyan Tea - Premium Grade',
    description: 'Black tea from Kenya\'s highlands. CTC processed, perfect for blending.',
    category: 'agriculture',
    price: 2800,
    currency: 'USD',
    moq: 100,
    country_of_origin: 'Kenya',
    seller_name: 'Kenya Tea Development Agency',
    verification_status: 'verified',
    afcfta_eligible: true,
    images: ['https://images.unsplash.com/photo-1556679343-c7306c1976bc?w=400&h=300&fit=crop'],
  },
  {
    product_id: 'prod_005',
    name: 'South African Citrus Fruits',
    description: 'Fresh oranges, lemons, and grapefruits. Export quality, sustainably farmed.',
    category: 'agriculture',
    price: 1500,
    currency: 'USD',
    moq: 500,
    country_of_origin: 'South Africa',
    seller_name: 'Cape Citrus Exporters',
    verification_status: 'verified',
    afcfta_eligible: true,
    images: ['https://images.unsplash.com/photo-1582979512210-99b6a53386f9?w=400&h=300&fit=crop'],
  },
  {
    product_id: 'prod_006',
    name: 'Moroccan Argan Oil',
    description: 'Pure organic argan oil for cosmetics and culinary use. Cold-pressed.',
    category: 'agro-processing',
    price: 8500,
    currency: 'USD',
    moq: 20,
    country_of_origin: 'Morocco',
    seller_name: 'Atlas Argan Cooperative',
    verification_status: 'verified',
    afcfta_eligible: true,
    images: ['https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=400&h=300&fit=crop'],
  },
  {
    product_id: 'prod_007',
    name: 'Nigerian Cement - Portland Grade',
    description: 'High-quality Portland cement for construction. Meets international standards.',
    category: 'construction',
    price: 120,
    currency: 'USD',
    moq: 1000,
    country_of_origin: 'Nigeria',
    seller_name: 'Dangote Industries',
    verification_status: 'verified',
    afcfta_eligible: true,
    images: ['https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=400&h=300&fit=crop'],
  },
  {
    product_id: 'prod_008',
    name: 'Egyptian Cotton Fabric',
    description: 'Premium long-staple Egyptian cotton fabric. Ideal for luxury textiles.',
    category: 'textiles',
    price: 45,
    currency: 'USD',
    moq: 100,
    country_of_origin: 'Egypt',
    seller_name: 'Nile Cotton Mills',
    verification_status: 'pending',
    afcfta_eligible: false,
    images: ['https://images.unsplash.com/photo-1620799139507-2a76f79a2f4d?w=400&h=300&fit=crop'],
  },
];

const MarketplacePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || 'all',
    country: searchParams.get('country') || 'all',
    verifiedOnly: false,
    afcftaOnly: false,
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category && filters.category !== 'all') params.append('category', filters.category);
      if (filters.country && filters.country !== 'all') params.append('country', filters.country);
      if (filters.verifiedOnly) params.append('verified_only', 'true');

      const response = await axios.get(`${API}/products?${params.toString()}`);
      
      // Use API products or fallback to samples
      let productList = response.data.length > 0 ? response.data : sampleProducts;
      
      // Apply client-side filters for demo
      if (filters.category && filters.category !== 'all') {
        productList = productList.filter(p => p.category === filters.category);
      }
      if (filters.country && filters.country !== 'all') {
        productList = productList.filter(p => p.country_of_origin === filters.country);
      }
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        productList = productList.filter(p => 
          p.name.toLowerCase().includes(searchTerm) || 
          p.description.toLowerCase().includes(searchTerm)
        );
      }
      if (filters.verifiedOnly) {
        productList = productList.filter(p => p.verification_status === 'verified');
      }
      if (filters.afcftaOnly) {
        productList = productList.filter(p => p.afcfta_eligible);
      }
      
      setProducts(productList);
    } catch (error) {
      console.error('Error fetching products:', error);
      // Fallback to sample products
      setProducts(sampleProducts);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (value && value !== 'all') {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      country: 'all',
      verifiedOnly: false,
      afcftaOnly: false,
    });
    setSearchParams({});
  };

  const activeFiltersCount = [
    filters.category !== 'all' ? filters.category : '',
    filters.country !== 'all' ? filters.country : '',
    filters.verifiedOnly,
    filters.afcftaOnly,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className={isAuthenticated ? 'flex' : ''}>
        {/* Sidebar for authenticated users */}
        {isAuthenticated && <DashboardSidebar />}

        <div className={isAuthenticated ? 'flex-1' : ''}>
          {/* Hero Banner */}
          <section className={`bg-charcoal text-white py-12 ${isAuthenticated ? 'mx-6 mt-6 rounded-lg' : ''}`} data-testid="marketplace-hero">
            <div className="container-main">
              <div className="max-w-2xl">
                <Badge className="bg-tradeGold text-black mb-4">{t('marketplace.hero.badge')}</Badge>
                <h1 className="text-3xl md:text-4xl font-bold mb-4 font-poppins">
                  {t('marketplace.hero.title')}
                </h1>
                <p className="text-gray-300 mb-6">
                  {t('marketplace.hero.subtitle')}
                </p>
                
                {/* Search Bar */}
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      placeholder={t('marketplace.search.placeholder')}
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="ps-12 h-12 bg-white text-charcoal"
                      data-testid="marketplace-search"
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    className="h-12 px-4 bg-white/10 border-white/20 text-white hover:bg-white hover:text-charcoal lg:hidden"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <SlidersHorizontal className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <div className={isAuthenticated ? 'p-6' : 'container-main py-8'}>
            <div className="flex gap-8">
              {/* Sidebar Filters - Desktop */}
          <aside className={`w-64 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-charcoal">{t('marketplace.filters.title')}</h3>
                  {activeFiltersCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="text-sm">
                      {t('common.clearAll')}
                    </Button>
                  )}
                </div>

                {/* Category Filter */}
                <div className="mb-6">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">{t('marketplace.filters.category')}</label>
                  <Select 
                    value={filters.category} 
                    onValueChange={(v) => handleFilterChange('category', v)}
                  >
                    <SelectTrigger data-testid="category-filter">
                      <SelectValue placeholder={t('marketplace.filters.allCategories')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('marketplace.filters.allCategories')}</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Country Filter */}
                <div className="mb-6">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">{t('marketplace.filters.country')}</label>
                  <Select 
                    value={filters.country} 
                    onValueChange={(v) => handleFilterChange('country', v)}
                  >
                    <SelectTrigger data-testid="country-filter">
                      <SelectValue placeholder={t('marketplace.filters.allCountries')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('marketplace.filters.allCountries')}</SelectItem>
                      {countries.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Checkbox Filters */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      id="verified"
                      checked={filters.verifiedOnly}
                      onCheckedChange={(checked) => handleFilterChange('verifiedOnly', checked)}
                      data-testid="verified-filter"
                    />
                    <label htmlFor="verified" className="text-sm cursor-pointer">
                      {t('marketplace.filters.verifiedOnly')}
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      id="afcfta"
                      checked={filters.afcftaOnly}
                      onCheckedChange={(checked) => handleFilterChange('afcftaOnly', checked)}
                      data-testid="afcfta-filter"
                    />
                    <label htmlFor="afcfta" className="text-sm cursor-pointer">
                      {t('marketplace.filters.afcftaOnly')}
                    </label>
                  </div>
                </div>

                {/* Category Quick Links */}
                <div className="mt-8 pt-6 border-t">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">{t('marketplace.filters.browseByCategory')}</h4>
                  <div className="space-y-2">
                    {categories.slice(0, 5).map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleFilterChange('category', cat.id)}
                        className={`flex items-center gap-2 w-full text-left text-sm px-3 py-2 rounded-lg transition-colors ${
                          filters.category === cat.id 
                            ? 'bg-continental/10 text-continental' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <cat.icon className="w-4 h-4" />
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Products Grid */}
          <main className="flex-1">
            {/* AI Market Insight */}
            <AIMarketInsight category={filters.category} country={filters.country} />
            
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-gray-600">
                  {t('marketplace.results.showing')} <span className="font-semibold text-charcoal">{products.length}</span> {t('marketplace.results.products')}
                  {filters.category && filters.category !== 'all' && (
                    <span> {t('marketplace.results.in')} <span className="font-medium">{categories.find(c => c.id === filters.category)?.name}</span></span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  data-testid="grid-view-btn"
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                  data-testid="list-view-btn"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Active Filters */}
            {activeFiltersCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {filters.category && filters.category !== 'all' && (
                  <Badge variant="secondary" className="gap-1">
                    {categories.find(c => c.id === filters.category)?.name}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('category', 'all')} />
                  </Badge>
                )}
                {filters.country && filters.country !== 'all' && (
                  <Badge variant="secondary" className="gap-1">
                    {filters.country}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('country', 'all')} />
                  </Badge>
                )}
                {filters.verifiedOnly && (
                  <Badge variant="secondary" className="gap-1">
                    Verified only
                    <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('verifiedOnly', false)} />
                  </Badge>
                )}
                {filters.afcftaOnly && (
                  <Badge variant="secondary" className="gap-1">
                    AfCFTA eligible
                    <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('afcftaOnly', false)} />
                  </Badge>
                )}
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-continental" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-charcoal mb-2">{t('marketplace.results.noProducts')}</h3>
                <p className="text-gray-500 mb-6">{t('marketplace.results.noProductsSubtitle')}</p>
                <Button onClick={clearFilters} variant="outline">
                  {t('marketplace.results.clearFilters')}
                </Button>
              </div>
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' 
                : 'space-y-4'
              } data-testid="products-grid">
                {products.map((product) => (
                  <ProductCard key={product.product_id} product={product} />
                ))}
              </div>
            )}
              </main>
            </div>
          </div>
        </div>
      </div>

      {!isAuthenticated && <Footer />}
    </div>
  );
};

export default MarketplacePage;
